import {
  Bell, Shield, Search, GitPullRequest, FileText, ArrowUpRight, Wrench,
  TrendingUp, Target, Lightbulb, AlertTriangle, CheckCircle2, XCircle, Info,
  type LucideIcon,
} from "lucide-react";

export type Severity = "low" | "medium" | "high" | "critical";
export type AlertStatus = "open" | "acknowledged" | "resolved";
export type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

export const SEVERITY_CONFIG: Record<Severity, { color: string; badge: BadgeVariant; label: string }> = {
  low: { color: "text-muted-foreground", badge: "outline", label: "Baixa" },
  medium: { color: "text-warning", badge: "secondary", label: "Média" },
  high: { color: "text-orange-500", badge: "default", label: "Alta" },
  critical: { color: "text-destructive", badge: "destructive", label: "Crítica" },
};

export const STATUS_CONFIG: Record<AlertStatus, { icon: LucideIcon; label: string; color: string }> = {
  open: { icon: Bell, label: "Aberto", color: "text-destructive" },
  acknowledged: { icon: AlertTriangle, label: "Reconhecido", color: "text-warning" },
  resolved: { icon: CheckCircle2, label: "Resolvido", color: "text-success" },
};

export const METRIC_OPTIONS = [
  { value: "churn_risk", label: "Risco de Evasão" },
  { value: "error_rate", label: "Taxa de Erro IA (%)" },
  { value: "message_drop", label: "Queda de Mensagens (%)" },
  { value: "usage_limit", label: "Limite de Uso (%)" },
];

export const CONDITION_OPTIONS = [
  { value: ">", label: "Maior que" },
  { value: ">=", label: "Maior ou igual" },
  { value: "<", label: "Menor que" },
  { value: "==", label: "Igual a" },
];

export const RECOMMENDATION_ICONS: Record<string, LucideIcon> = {
  critical: XCircle, warning: AlertTriangle, success: CheckCircle2, insight: Lightbulb, info: Info,
};

export const RECOMMENDATION_COLORS: Record<string, string> = {
  critical: "border-destructive/30 bg-destructive/5",
  warning: "border-orange-500/30 bg-orange-500/5",
  success: "border-green-500/30 bg-green-500/5",
  insight: "border-primary/30 bg-primary/5",
  info: "border-muted-foreground/30 bg-muted/50",
};

export const PIE_COLORS = [
  "hsl(var(--destructive))",
  "hsl(var(--warning))",
  "hsl(var(--muted-foreground))",
  "hsl(var(--primary))",
];

export const MODEL_STATUS_COLORS: Record<string, BadgeVariant> = {
  approved: "default", experimental: "secondary", deprecated: "outline", blocked: "destructive",
};

export const PROPOSAL_STEPS = ["draft", "pending", "approved", "applied"] as const;

export const PROPOSAL_STATE_COLORS: Record<string, BadgeVariant> = {
  draft: "outline", pending: "secondary", candidate: "secondary",
  approved: "default", applied: "default", reverted: "destructive", rejected: "destructive",
};

export type AgentDef = {
  id: string;
  name: string;
  icon: LucideIcon;
  desc: string;
  color: string;
  fn: string;
  needs?: "audit_id" | "incident_id" | "alert_id";
};

export const AGENTS: AgentDef[] = [
  { id: "audit", name: "Audit", icon: Search, desc: "Audita prompts, modelos e skills", color: "text-blue-500", fn: "sentinel-audit" },
  { id: "evaluate", name: "Evaluate", icon: Shield, desc: "Avalia regras e dispara alertas", color: "text-primary", fn: "sentinel-evaluate" },
  { id: "propose", name: "Propose", icon: GitPullRequest, desc: "Gera propostas de melhoria", color: "text-purple-500", fn: "sentinel-propose", needs: "audit_id" },
  { id: "recommend", name: "Recommend", icon: Lightbulb, desc: "Recomendações de inteligência", color: "text-yellow-500", fn: "sentinel-recommend" },
  { id: "report", name: "Report", icon: FileText, desc: "Gera relatórios executivos", color: "text-emerald-500", fn: "sentinel-report" },
  { id: "escalate", name: "Escalate", icon: ArrowUpRight, desc: "Escala alertas não reconhecidos", color: "text-orange-500", fn: "sentinel-escalate" },
  { id: "remediate", name: "Remediate", icon: Wrench, desc: "Remediação automática segura", color: "text-rose-500", fn: "sentinel-remediate", needs: "alert_id" },
  { id: "forecast", name: "Forecast", icon: TrendingUp, desc: "Previsões de volume e custo", color: "text-cyan-500", fn: "sentinel-forecast" },
  { id: "root-cause", name: "Root Cause", icon: Target, desc: "Análise de causa raiz com IA", color: "text-indigo-500", fn: "sentinel-root-cause", needs: "incident_id" },
];

export const SENTINEL_TABS = [
  { value: "overview", label: "Visão Geral" },
  { value: "alerts", label: "Alertas" },
  { value: "audits", label: "Auditorias" },
  { value: "proposals", label: "Propostas" },
  { value: "forecasts", label: "Forecasts" },
  { value: "reports", label: "Relatórios" },
  { value: "config", label: "Configuração" },
] as const;

export type SentinelTab = typeof SENTINEL_TABS[number]["value"];
